/**
 *
 * @Copyright 2024 UNLOCKIT DECENTRALIZATION, LDA
 * Development by VOID Software, SA
 *
 */

import { FunctionComponent, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ReactComponent as ContractsSvg } from '../../assets/images/businesses.svg';
import { ReactComponent as ChatSvg } from '../../assets/images/chat.svg';
import { ReactComponent as HomeSvg } from '../../assets/images/home.svg';
import { ReactComponent as Logo } from '../../assets/images/logo-horizontal.svg';
import { ReactComponent as OrganizationSvg } from '../../assets/images/people.svg';
import { ReactComponent as WorkflowSvg } from '../../assets/images/workflow.svg';
import { useCornerstoneApi } from '../../api';
import { AppRoute } from '../../constants/routes';
import { MenuBarItem } from '../../types/menu';
import { OrganizationsContext, withOrganizationsContext } from '../controllers/OrganizationsContext';
import { TranslationContext, withTranslationContext } from '../controllers/TranslationContext';

type OwnProps = TranslationContext & OrganizationsContext;

const MenuBarComponent: FunctionComponent<OwnProps> = (props) => {
    const { organizationSelected, t } = props;
    const api = useCornerstoneApi();
    const [showLegalAdvisor, setShowLegalAdvisor] = useState(false);
    const hasOrganization = Boolean(organizationSelected?.organization);

    useEffect(() => {
        let active = true;

        const fetchAccess = async () => {
            try {
                if (!hasOrganization) {
                    if (active) {
                        setShowLegalAdvisor(false);
                    }
                    return;
                }
                const { data } = await api.get<{ access?: { show?: boolean } }>('/fleck/access');
                if (active) {
                    setShowLegalAdvisor(Boolean(data?.access?.show));
                }
            } catch (error) {
                if (active) {
                    setShowLegalAdvisor(false);
                }
            }
        };

        fetchAccess();

        return () => {
            active = false;
        };
    }, [hasOrganization]);

    const menuBarItems: MenuBarItem[] = organizationSelected?.organization
        ? [
            {
                title: 'home',
                icon: <HomeSvg />,
                link: AppRoute.Index,
            },
            {
                title: 'organization',
                icon: <OrganizationSvg />,
                link: AppRoute.OrganizationBillingDashboard,
            },
            {
                title: 'contracts',
                icon: <ContractsSvg />,
                link: AppRoute.Contracts,
            },
            {
                title: 'workflows',
                icon: <WorkflowSvg />,
                link: AppRoute.OrganizationWorkflows,
            },
        ] : [
            {
                title: 'home',
                icon: <HomeSvg />,
                link: AppRoute.Index,
            },
            {
                title: 'contracts',
                icon: <ContractsSvg />,
                link: AppRoute.Contracts,
            },
            {
                title: 'workflows',
                icon: <WorkflowSvg />,
                link: AppRoute.Workflows,
            },
        ];

    if (showLegalAdvisor) {
        menuBarItems.push({
            title: 'realEstateLegalAdvisor',
            label: 'Veritas',
            badgeLabel: t('menuBar.newTag'),
            icon: <ChatSvg className="menu-bar__icon--chat" />,
            link: AppRoute.RealEstateLegalAdvisor,
        });
    }

    return (
        <div className="menu-bar" data-testid="menu-bar">
            <div className="menu-bar__logo-wrap_desktop">
                <Logo />
            </div>
            <div className="menu-bar__wrap-list">
                <ul className="menu-bar__wrap-list__list dark-custom-scrollbar">
                    {menuBarItems.map((item) => (
                        <NavLink to={item.link} key={item.title} end>
                            <li className="menu-bar__list__item">
                                <div className="menu-bar__list__item__icon-wrap">
                                    {item.icon}
                                </div>
                                <span className="menu-bar__list__item__title" data-badge={item.badgeLabel}>
                                    {item.label ?? t(`menuBar.${item.title}`)}
                                </span>
                            </li>
                        </NavLink>
                    ))}
                </ul>
            </div>
            {/* Bottom for now is just for styling purpose */}
            <div className="menu-bar__bottom" />
        </div>
    );
};

export const MenuBar = withOrganizationsContext(withTranslationContext(MenuBarComponent));
